import { PaymentType } from '@afterpay/types'
import {
  portalAction,
  PortalThunk,
  profilingIdSelector,
  tokenSelector,
  checkoutModeSelector,
  donationSelector,
  selectedAutopayEnabledSelector,
  selectedPaymentTypeSelector,
  convergenceCohortSelector,
} from '../../index'
import {
  Checkout,
  ConfirmCheckoutRequest,
  ConfirmedCheckout,
  CrossRegionUserDetails,
  CrossRegionRedirection,
  UpdatedCheckout,
  UpdateCheckoutRequest,
  UpdateCheckoutData,
  applyInitialData,
  PaymentOption,
  UpdatedPaymentOption,
  PaymentMethodType,
  OrderTransactionDocumentType,
  CreateDocumentsRequest,
  RetrieveCardsResponse,
  RetrieveCardsRequest,
  CashAppObject,
  VerifyEFARobocallEventRequest,
  ConfirmCheckoutRequestParams,
  THEME_COOKIE_NAME,
} from '../../../model'
import { ErrorCode, IdentityNationalIDNumberRequest, IdentityNationalIDNumberResponse, CheckoutMode } from '@afterpaytouch/portal-api/types'
import {
  checkoutInitialDataSelector,
  isPickupSelector,
  startedCheckoutSelector,
  creditCheckAgreeSelector,
  isEnabledPBIAutoPaySelector,
  termsDisclosureAgreeSelector,
  checkoutAccountOpeningDisclosureSelector,
  confirmTermsViewedSelector,
} from '../hooks'
import { authTokenSelector, cashCheckoutTokenSelector } from '../../auth/user/hooks'
import { selectedPaymentMethod as selectedPaymentMethodSelector } from '../../topaz/hooks'
import { fetchVerification } from '@afterpaytouch/event-verify'
import { api, app } from '../../../env'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { parseCookies } from 'nookies'
import { Theme } from '@afterpaytouch/core'

interface BankAccountConfirmCheckoutRequest {
  paymentMethodId: number
  paymentMethodType: PaymentMethodType.ACH_BANK_ACCOUNT
}

interface CashAppPayConfirmCheckoutRequest {
  paymentMethodId: number
  paymentMethodType: PaymentMethodType.CASH_APP_PAY
}

export const getCheckout: PortalThunk<void, Checkout> = portalAction('checkout/get', async (_, thunk) => {
  const cashCheckoutToken = cashCheckoutTokenSelector(thunk.getState())
  const headers = {
    ...(cashCheckoutToken != null && { 'x-cash-token': cashCheckoutToken }),
  }
  const token = tokenSelector(thunk.getState())
  return await thunk.extra.portalApi().get<Checkout>(`/portal/consumers/checkout/${token}`, headers)
})

export const addCashCustomerDetails: PortalThunk<void, CashAppObject | undefined> = portalAction('checkout/cash', async (_, thunk) => {
  const cashCheckoutToken = cashCheckoutTokenSelector(thunk.getState())
  const headers = {
    ...(cashCheckoutToken !== null && { 'x-cash-token': cashCheckoutToken }),
  }
  const token = tokenSelector(thunk.getState())
  return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/cash`, null, headers)
})

export const updateCheckout: PortalThunk<UpdateCheckoutData, UpdatedCheckout> = portalAction('checkout/update', async (request, thunk) => {
  const token = tokenSelector(thunk.getState())
  const profilingSessionId = profilingIdSelector(thunk.getState())
  const isPickUp = isPickupSelector(thunk.getState())
  const payload: UpdateCheckoutRequest = {
    profilingSessionId,
    isPickUp,
    ...request,
  }
  return await thunk.extra.portalApi().put(`/portal/consumers/checkout/${token}/update`, payload)
})

export const confirmCheckout: PortalThunk<ConfirmCheckoutRequestParams | undefined, ConfirmedCheckout> = portalAction(
  'checkout/confirm',
  async (params, thunk) => {
    const { shouldUpdatePreferredPaymentDay = false } = params ?? {}
    const paymentType = selectedPaymentTypeSelector(thunk.getState())
    const selectedPaymentMethod = selectedPaymentMethodSelector(thunk.getState())
    const token = tokenSelector(thunk.getState())
    const profilingSessionId = profilingIdSelector(thunk.getState())
    const { donationAmount } = donationSelector(thunk.getState())
    const startedCheckout = startedCheckoutSelector(thunk.getState())
    const donationCampaign = startedCheckout?.donationCampaign
    const PCLAutopay = selectedAutopayEnabledSelector(thunk.getState())
    const PBIAutopay = isEnabledPBIAutoPaySelector(thunk.getState())
    const creditCheckAgree = creditCheckAgreeSelector(thunk.getState())
    const termsDisclosureAgree = termsDisclosureAgreeSelector(thunk.getState())
    const accountOpeningDisclosureConsent = checkoutAccountOpeningDisclosureSelector(thunk.getState())
    const checkoutCohort = convergenceCohortSelector(thunk.getState())
    const confirmTermsViewed = confirmTermsViewedSelector(thunk.getState())
    const { APP_VERSION } = app

    const cookies = parseCookies()
    const checkoutTheme = cookies[THEME_COOKIE_NAME] as Theme
    const termsViewed = `${confirmTermsViewed ?? ''}-${APP_VERSION}`

    /**
     * If paymentType is undefined, it means the user is in the PBI flow
     * If paymentType is defined, it means the user is in the PCL flow, but the paymentType can be PCL or PBI
     */
    const autoPayMap: Record<PaymentType, boolean | undefined> = {
      [PaymentType.PBI]: PBIAutopay,
      [PaymentType.PCL]: PCLAutopay,
      [PaymentType.PAD]: undefined,
    }
    const autopay = paymentType ? autoPayMap[paymentType] : PBIAutopay

    const isBankAccountOrCashAppPay =
      typeof selectedPaymentMethod !== 'undefined' &&
      (selectedPaymentMethod?.type === PaymentMethodType.ACH_BANK_ACCOUNT || selectedPaymentMethod?.type === PaymentMethodType.CASH_APP_PAY)

    const bankAccountOrCashAppPayObject = (): BankAccountConfirmCheckoutRequest | CashAppPayConfirmCheckoutRequest | undefined => {
      if (!isBankAccountOrCashAppPay) {
        return
      }
      const paymentMethodId = 'account' in selectedPaymentMethod ? selectedPaymentMethod.account.id : selectedPaymentMethod.cashAppPay.id

      return {
        paymentMethodId,
        paymentMethodType: selectedPaymentMethod.type,
      }
    }

    const donation = { donation: donationCampaign }

    const checkoutClient = 'checkout-redesign-v1'
    const payload: ConfirmCheckoutRequest = {
      profilingSessionId,
      checkoutClient,
      paymentType,
      checkoutCohort,
      checkoutTheme,
      ...(donationAmount ? donation : null),
      ...{ autopay },
      ...{ creditCheckAgree },
      ...(shouldUpdatePreferredPaymentDay && { shouldUpdatePreferredPaymentDay }),
      ...bankAccountOrCashAppPayObject(),
      ...{ termsDisclosureAgree },
      ...(valueIsNotNullOrUndefined(accountOpeningDisclosureConsent) && {
        accountOpeningDisclosureConsent,
        termsViewed,
      }),
    }
    const headers = {
      ...(window.xprops && typeof window.xprops.apAppDeviceId === 'string' && { 'x-ap-device-id': window.xprops.apAppDeviceId }),
    }
    return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/confirm`, payload, headers)
  }
)

export const crossRegionUserSignIn: PortalThunk<CrossRegionUserDetails, CrossRegionRedirection> = portalAction(
  'checkout/crossRegionUserSignIn',
  async (request, thunk) => {
    const initialData = checkoutInitialDataSelector(thunk.getState())
    const checkoutMode = checkoutModeSelector(thunk.getState())
    const response: CrossRegionRedirection = await thunk.extra
      .portalApi()
      .post(`/portal/consumers/checkout/${initialData.token}/crossRegionUserSignIn`, request)
    const crossRegionCheckoutUrl = response.crossRegionCheckoutUrl

    const transformedInitialData =
      checkoutMode === CheckoutMode.STANDARD
        ? {
            ...initialData,
            shippingOptionRequired: false, // INC-1037 Make sure old checkout does not expect a shipping option for standard checkout
          }
        : initialData

    return { crossRegionCheckoutUrl: applyInitialData(crossRegionCheckoutUrl, transformedInitialData) }
  }
)

export const getPaymentOptions: PortalThunk<void, PaymentOption[]> = portalAction('checkout/paymentOptions', async (_, thunk) => {
  const token = tokenSelector(thunk.getState())
  let response: PaymentOption[]
  try {
    response = await thunk.extra.portalApi().get(`/portal/consumers/checkout/${token}/payment-options`)
  } catch (error) {
    console.log(error)
    if (error?.response?.status === 429) {
      error.response.data = { errorCode: ErrorCode.TooManyRequests }
    }
    throw error
  }
  return response
})

export const updatePaymentOptions: PortalThunk<void, PaymentOption[]> = portalAction('checkout/updatePaymentOptions', async (_, thunk) => {
  const token = tokenSelector(thunk.getState())
  return await thunk.extra.portalApi().put(`/portal/consumers/checkout/${token}/payment-options`, null)
})

export const updatePaymentOption: PortalThunk<number | undefined, UpdatedPaymentOption> = portalAction(
  'checkout/paymentOptions/paymentOptionId',
  async (paymentOptionId, thunk) => {
    const token = tokenSelector(thunk.getState())
    return await thunk.extra.portalApi().put(`/portal/consumers/checkout/${token}/payment-options/${paymentOptionId}`, null)
  }
)

export const createDocuments: PortalThunk<CreateDocumentsRequest, Record<OrderTransactionDocumentType, string>> = portalAction(
  'checkout/documents',
  async (request, thunk) => {
    const token = tokenSelector(thunk.getState())
    return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/documents`, request)
  }
)

export const retrieveCards: PortalThunk<void, RetrieveCardsResponse> = portalAction('checkout/retrieveCards', async (request, thunk) => {
  const token = tokenSelector(thunk.getState())
  const profilingSessionId = profilingIdSelector(thunk.getState())
  const payload: RetrieveCardsRequest = {
    profilingSessionId,
  }
  return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/cards`, payload)
})

export const identityNationalIDNumber: PortalThunk<IdentityNationalIDNumberRequest, IdentityNationalIDNumberResponse> = portalAction(
  'checkout/verifyNationalIdNumber',
  async (request, thunk) => {
    const token = tokenSelector(thunk.getState())
    return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/identities/national-id-number`, request)
  },
  true
)

export const verifyEFARobocallEvent: PortalThunk<VerifyEFARobocallEventRequest, void> = portalAction(
  'checkout/efa/robo-call',
  async (verifyEFARobocallEventRequest, thunk) => {
    const token = tokenSelector(thunk.getState())
    const { onSuccess, onError } = verifyEFARobocallEventRequest
    return await fetchVerification({
      url: encodeURI(`${api.PORTAL_API_BASE_URL}/portal/consumers/checkout/${token}/efa/robo-call`),
      onSuccess: () => {
        onSuccess()
      },
      onError: (d) => {
        onError(d)
      },
      onWaiting: () => {},
      headers: {
        'x-auth-token': authTokenSelector(thunk.getState()),
      },
    })
  }
)
